import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import {
  useParams,
  Outlet,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { Layout, message } from 'antd';
import { useMixpanel } from 'react-mixpanel-browser';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  setAssetVariant,
  createNewComment,
  removeComment,
  editComment,
  removeCommentAttachment,
  setAsset,
  setCommentLike
} from '@redux/actions/mediaViewerAction';
import { useCurrentWorkspace } from '@hooks/workspace';

import MediaViewerHeader from '@components/MediaViewerHeader';
import WorkspacesPanel from '@components/WorkspacesPanel';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import {
  AssetVersionCommentMentionDto,
  CreateAssetVersionCommentDto,
  PageDto,
  ReplyAssetVersionCommentDto
} from '@api/Api';
import {
  assetInternalCommentsCreateAssetComment,
  assetInternalCommentsDeleteAssetComment,
  assetInternalCommentsGetAssetComment,
  assetInternalCommentsListCommentPotentialMentions,
  assetInternalCommentsReplyAssetComment,
  assetInternalCommentsSetAssetCommentLike,
  assetInternalCommentsUpdateAssetComment,
  assetSetRecentlyOpened
} from '@api/Asset';
import { useTypedSelector } from '@hooks';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { MixpanelService, MixpanelEventType } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';

import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { ReactComponent as ErrorSvg } from '@assets/icons/error.svg';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { apiClient } from '@api/client';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import type { MediaViewerHeaderProps } from '@components/MediaViewerHeader/MediaViewerHeader';
import { useAssetQuery } from '@hooks/queries/assets';
import { useInternalCommentsAssetReviewQuery } from '@hooks/queries/asset';
import {
  MediaViewerOutletContextState,
  CreateAssetCommentParams,
  UpdateAssetCommentParams,
  SetAssetCommentLikeParams
} from '@layouts/types';

const { Header, Content, Sider } = Layout;

type MediaViewerLayoutProps = {
  headerProps?: MediaViewerHeaderProps;
};

function MediaViewerLayout({ headerProps = {} }: MediaViewerLayoutProps) {
  const { onStartTour } = useOnboardingProcess();
  const { assetId, variantId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const [searchParams] = useSearchParams();
  const { user } = useAuth(false, 'internal');
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const workspacesListSiderWidth = 65;
  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const assetVersionId = selectedVersion?.id;
  const prevVersionId = useRef(assetVersionId || null);

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useInternalCommentsAssetReviewQuery(
      {
        assetVersionId: variantId || ''
      },
      {
        keepPreviousData: assetVersionId === prevVersionId.current,
        enabled: !!variantId
      }
    );

  if (assetVersionId !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = assetVersionId || null;
  }

  useEffect(() => {
    if (!assets) return;
    if (!selectedVersion) return;
    if (assets.id !== assetId) return;
    const versionExists = assets.asset?.versions.find(
      (it) => it.id === selectedVersion?.id
    );
    if (versionExists) return;
    navigate(`/media-viewer/${assets.id}/${assets.asset?.versions[0].id}`);
  }, [navigate, assetId, assets, selectedVersion]);

  useEffect(() => {
    if (!assetId || !assetVersionId) return;
    (async () => {
      await assetSetRecentlyOpened({ assetId, assetVersionId });
    })();
  }, [assetId, assetVersionId]);

  const outletContext = useMemo<MediaViewerOutletContextState>(
    () => ({
      addNewComment: async (
        data: CreateAssetCommentParams,
        parentId: string
      ) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await assetInternalCommentsReplyAssetComment(
              data as ReplyAssetVersionCommentDto
            );
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await assetInternalCommentsCreateAssetComment(
              data as CreateAssetVersionCommentDto
            );
            newComment = comment;
            if (user) {
              await MixpanelService.track(
                mixpanel,
                user.id,
                MixpanelEventType.MANAGE_ASSETS,
                {
                  addToFavorite: false,
                  deleteAsset: false,
                  newAsset: false,
                  externalLinkShare: false,
                  approvalRouting: false,
                  newCommentAsset: true,
                  annotationLeft: !!comment.annotations,
                  taskFromComment: false
                }
              );
            }
          }
          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id: string) => {
        try {
          await removeComment(id);
          await assetInternalCommentsDeleteAssetComment({ id });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data: UpdateAssetCommentParams) => {
        try {
          const {
            data: { comment }
          } = await assetInternalCommentsUpdateAssetComment(data);
          editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data: SetAssetCommentLikeParams) => {
        try {
          await setCommentLike(data, user);
          await assetInternalCommentsSetAssetCommentLike(data);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      getPotentialMentions: async ({
        assetVersionId,
        searchQuery
      }: {
        assetVersionId: string;
        searchQuery?: string;
      }): Promise<AssetVersionCommentMentionDto[]> => {
        const {
          data: { edges }
        }: {
          data: PageDto & { edges: { node: AssetVersionCommentMentionDto }[] };
        } = await assetInternalCommentsListCommentPotentialMentions({
          assetVersionId,
          searchQuery
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({
        assetVersionId,
        searchQuery
      }: {
        assetVersionId: string;
        searchQuery?: string;
      }): Promise<AssetVersionCommentMentionDto[]> => {
        const {
          data: { edges }
        }: {
          data: PageDto & { edges: { node: AssetVersionCommentMentionDto }[] };
        } =
          await apiClient.asset.assetInternalCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsList || [],
      workspace: currentWorkspace
    }),
    [
      currentWorkspace,
      variantId,
      isLoadingCommentsList,
      mixpanel,
      user,
      commentsList
    ]
  );

  const assetQuery = useAssetQuery(
    { assetId: assetId ?? '' },
    { enabled: !!assetId }
  );

  useLayoutEffect(() => {
    dispatch(toggleApprovalModal(null));
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_STATE
    });
  }, [assetId, dispatch]);

  useLayoutEffect(() => {
    const asset = assetQuery.data ?? null;
    dispatch(setAsset(asset));
    const version =
      asset?.asset?.versions.find((v) => v.id === variantId) ??
      asset?.asset?.versions[0];
    dispatch(setAssetVariant(version?.id ?? null));
    if (asset?.workspaceId) {
      setCurrentWorkspace(asset.workspaceId);
    }
  }, [assetQuery.data, dispatch, variantId, setCurrentWorkspace]);

  useLayoutEffect(() => {
    if (!selectedVersion?.id) return;
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_VERSION_STATE
    });
  }, [dispatch, selectedVersion?.id]);

  useEffect(() => {
    if (!assetQuery.data) return;
    const startTour = searchParamsRef.current.get('startTour') ?? undefined;
    if (startTour === 'route-for-approval') {
      onStartTour({ type: startTour as any, theme: 'dark' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetQuery.isLoading]);

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetId) return;
    if (msg.payload.assetVersionId !== variantId) return;
    if (msg.causedByMe) return;
    const {
      data: { comment }
    } = await assetInternalCommentsGetAssetComment({
      id: msg.payload.commentId
    });
    await createNewComment(comment, variantId || '');
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetId) return;
    if (msg.payload.assetVersionId !== variantId) return;
    if (msg.causedByMe) return;
    await removeComment(msg.payload.commentId);
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== assetId) return;
      const {
        data: { comment }
      } = await assetInternalCommentsGetAssetComment({
        id: lastMessage.payload.target.commentId
      });
      await editComment(comment);
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== assetId) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== assetId) return;
    if (msg.causedByMe) return;
    await removeCommentAttachment({
      commentId: msg.payload.target.commentId,
      attachmentId: msg.payload.attachmentId
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];

      const {
        data: { comment }
      } = await assetInternalCommentsGetAssetComment({
        id: lastMessage.payload.commentId
      });
      await editComment(comment);
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.assetVersionId !== variantId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      const { data: newAsset } = await assetQuery.refetch();
      if (!newAsset) return;
      const newVersion = messages.some(
        (x) => x.payload.reason === 'new-version'
      );
      if (newVersion) {
        const lastVersionId = newAsset?.asset?.versions[0].id;
        navigate(`/media-viewer/${newAsset?.id}/${lastVersionId}`);
        message.success({
          content: `New version was uploaded ${moment(new Date()).format(
            'MMM DD, YYYY'
          )}`,
          className: 'message-dark-modal',
          icon: <SuccessSvg className="anticon" />
        });
      }
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (
        msg.causedByMe &&
        msg.payload.reason !== 'conversion-status-changed' &&
        msg.payload.reason !== 'new-version'
      )
        return false;
      return true;
    },
    'asset-updated'
  );

  return (
    <Layout
      style={{ minHeight: '100vh', background: '#1A1C1E', color: '#fff' }}
    >
      <Sider
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0
        }}
        className="workspaces_sider b-bg-dark"
        width={workspacesListSiderWidth}
      >
        <WorkspacesPanel />
      </Sider>
      <Layout
        className="site-layout"
        style={{ background: '#1A1C1E', marginLeft: workspacesListSiderWidth }}
      >
        {!assetQuery.isError && (
          <Header
            className="site-layout-background b-background-black"
            style={{
              width: `calc(100% - ${workspacesListSiderWidth}px)`,
              padding: 0
            }}
          >
            <MediaViewerHeader {...headerProps} />
          </Header>
        )}

        <Content style={{ marginTop: 70 }}>
          <div style={{ minHeight: 360, height: '100%' }}>
            {assetQuery.isError ? (
              <div className="media_viewer_content_deleted">
                <ErrorSvg />
                <h3>Oops…looks like that media was deleted.</h3>
              </div>
            ) : (
              <Outlet context={outletContext} />
            )}
          </div>
        </Content>
      </Layout>
      <ToastContainer
        transition={Slide}
        closeOnClick={false}
        closeButton={false}
        position="top-center"
        theme="dark"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        progressClassName="fancy-progress-bar"
      />
    </Layout>
  );
}

export default MediaViewerLayout;
